import { Button, Form, Offcanvas, Tab, Tabs } from 'react-bootstrap';
import CodeMirror from '@uiw/react-codemirror';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'context/ThemeContext';
import ValidateJSON from 'components/others/ValidateJSON';
import { getExtensions } from 'utils/helpers/codemiror/getExtensions';
import BusinessModeFormHelp from './BusinessModeFormHelp';

interface BusinessModeCustomEditorProps {
    show: boolean;
    tab: 'form' | 'help';
    value: string;
    onHide: () => void;
    onTabChange: (tab: 'form' | 'help') => void;
    onValueChange: (value: string) => void;
    onApply: () => void;
}

const BusinessModeCustomEditor = ({
    show,
    tab,
    value,
    onHide,
    onTabChange,
    onValueChange,
    onApply,
}: BusinessModeCustomEditorProps) => {
    const { t } = useTranslation(['Settings', 'common']);
    const { darkTheme } = useTheme();

    return (
        <Offcanvas
            placement="end"
            show={show}
            onHide={onHide}
            className="w-50"
            style={{ minWidth: '480px' }}
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>{t('businessMode.editor.title')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="d-flex flex-column">
                <Tabs
                    activeKey={tab}
                    onSelect={(eventKey) => onTabChange((eventKey || 'form') as 'form' | 'help')}
                    variant="underline"
                    className="mb-3 invoice-tab border-bottom flex-shrink-0"
                >
                    <Tab eventKey="form" title={t('businessMode.editor.tabs.form')}>
                        <div className="pt-2">
                            <Form.Group className="mb-2">
                                <Form.Text className="text-muted">{t('businessMode.editor.hint')}</Form.Text>
                            </Form.Group>
                            <CodeMirror
                                value={value}
                                onChange={onValueChange}
                                theme={darkTheme ? 'dark' : 'light'}
                                height="calc(100vh - 290px)"
                                extensions={[getExtensions('json')]}
                            />
                            <ValidateJSON json={value} />
                        </div>
                    </Tab>
                    <Tab eventKey="help" title={t('businessMode.editor.tabs.help')}>
                        <div className="pt-2">
                            <BusinessModeFormHelp />
                        </div>
                    </Tab>
                </Tabs>
                <div className="d-flex justify-content-end gap-2 mt-auto pt-3">
                    <Button variant="light" onClick={onHide}>
                        {t('common:buttons.cancel')}
                    </Button>
                    <Button onClick={onApply}>{t('businessMode.editor.apply')}</Button>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default BusinessModeCustomEditor;
